import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { MushakService } from '../../services/mushak.service';
import { DocumentTaxComponent } from '../../interfaces/indirectTax.interface';
import { formatMoney } from '../../utils/format';

/**
 * Per-component tax of a coded sales invoice / supplier bill (e.g. "Supplementary Duty 10%: 43,500; VAT 15%: 71,775").
 * Failure or an empty answer (legacy documents, module off, no permission) yields no components, and the caller keeps
 * showing the single header tax figure.
 */
export function useDocumentTaxBreakdown(
  kind: 'sales-invoice' | 'supplier-bill',
  id: string | null | undefined,
  enabled: boolean,
): DocumentTaxComponent[] {
  const [components, setComponents] = useState<DocumentTaxComponent[]>([]);
  useEffect(() => {
    if (!id || !enabled) { setComponents([]); return undefined; }
    let cancelled = false;
    Promise.resolve(MushakService.getDocumentTaxBreakdown(kind, id))
      .then(b => { if (!cancelled) setComponents(b?.components ?? []); })
      .catch(() => { if (!cancelled) setComponents([]); });
    return () => { cancelled = true; };
  }, [kind, id, enabled]);
  return components;
}

const pct = (n: number) => `${Number(n)}%`;

/** The "Tax" cell of a document header: the component rows when known, else the single total. */
export const DocumentTaxCell: React.FC<{ total: number; components: DocumentTaxComponent[] }> = ({ total, components }) => {
  if (components.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">Tax Amount</Typography>
        <Typography>{formatMoney(total)}</Typography>
      </Box>
    );
  }
  return (
    <Box data-testid="tax-breakdown">
      <Typography variant="body2" color="text.secondary">Tax ({formatMoney(total)})</Typography>
      {components.map(c => (
        <Typography key={`${c.name}-${c.ratePct}-${c.recoverable}`}>
          {c.name} {pct(c.ratePct)}: {formatMoney(c.taxAmount)}
        </Typography>
      ))}
    </Box>
  );
};
